'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FilterOption {
  id: string;
  label: string;
  count: number;
}

interface FilterSection {
  title: string;
  options: FilterOption[];
}

interface FilterSidebarProps {
  onFilterChange: (filters: SelectedFilters) => void;
  selectedFilters: SelectedFilters;
}

export interface SelectedFilters {
  destinations: string[];
  durations: string[];
  priceRanges: string[];
  travelStyles: string[];
}

const FilterSidebar = ({ onFilterChange, selectedFilters }: FilterSidebarProps) => {
  const [expandedSections, setExpandedSections] = useState<string[]>([
    'destinations',
    'durations',
    'priceRanges',
    'travelStyles'
  ]);

  const filterSections: Record<string, FilterSection> = {
    destinations: {
      title: 'Destinations',
      options: [
        { id: 'europe', label: 'Europe', count: 24 },
        { id: 'asia', label: 'Asia', count: 18 },
        { id: 'americas', label: 'Americas', count: 15 },
        { id: 'africa', label: 'Africa', count: 12 },
        { id: 'oceania', label: 'Oceania', count: 8 }
      ]
    },
    durations: {
      title: 'Duration',
      options: [
        { id: '3-5', label: '3-5 Days', count: 22 },
        { id: '6-9', label: '6-9 Days', count: 28 },
        { id: '10-14', label: '10-14 Days', count: 19 },
        { id: '15+', label: '15+ Days', count: 8 }
      ]
    },
    priceRanges: {
      title: 'Price Range',
      options: [
        { id: 'budget', label: 'Under $2,000', count: 15 },
        { id: 'moderate', label: '$2,000 - $5,000', count: 32 },
        { id: 'luxury', label: '$5,000 - $10,000', count: 21 },
        { id: 'ultra', label: '$10,000+', count: 9 }
      ]
    },
    travelStyles: {
      title: 'Travel Style',
      options: [
        { id: 'adventure', label: 'Adventure', count: 18 },
        { id: 'cultural', label: 'Cultural', count: 25 },
        { id: 'relaxation', label: 'Relaxation', count: 20 },
        { id: 'luxury', label: 'Luxury', count: 16 },
        { id: 'family', label: 'Family', count: 14 }
      ]
    }
  };

  const toggleSection = (section: string) => {
    setExpandedSections(prev =>
      prev.includes(section)
        ? prev.filter(s => s !== section)
        : [...prev, section]
    );
  };

  const handleFilterToggle = (category: keyof SelectedFilters, value: string) => {
    const currentFilters = selectedFilters[category];
    const newFilters = currentFilters.includes(value)
      ? currentFilters.filter(f => f !== value)
      : [...currentFilters, value];

    onFilterChange({
      ...selectedFilters,
      [category]: newFilters
    });
  };

  const clearAllFilters = () => {
    onFilterChange({
      destinations: [],
      durations: [],
      priceRanges: [],
      travelStyles: []
    });
  };

  const getActiveFilterCount = () => {
    return Object.values(selectedFilters).reduce((sum, arr) => sum + arr.length, 0);
  };

  return (
    <div className="bg-card rounded-lg shadow-brand p-6 sticky top-20">
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-heading font-semibold text-lg text-foreground">Filters</h3>
        {getActiveFilterCount() > 0 && (
          <button
            onClick={clearAllFilters}
            className="text-sm text-coral hover:text-[#FF5722] font-medium transition-colors duration-200"
          >
            Clear All
          </button>
        )}
      </div>

      <div className="space-y-6">
        {Object.entries(filterSections).map(([key, section]) => (
          <div key={key} className="border-b border-border pb-4 last:border-b-0">
            <button
              onClick={() => toggleSection(key)}
              className="flex items-center justify-between w-full text-left mb-3"
            >
              <span className="font-heading font-medium text-foreground">
                {section.title}
              </span>
              <Icon
                name={expandedSections.includes(key) ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                size={20}
                className="text-muted-foreground"
              />
            </button>

            {expandedSections.includes(key) && (
              <div className="space-y-2">
                {section.options.map(option => {
                  const isSelected = selectedFilters[key as keyof SelectedFilters].includes(option.id);
                  return (
                    <label
                      key={option.id}
                      className="flex items-center justify-between cursor-pointer group"
                    >
                      <div className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleFilterToggle(key as keyof SelectedFilters, option.id)}
                          className="w-4 h-4 rounded border-border text-primary focus:ring-2 focus:ring-primary focus:ring-offset-0 cursor-pointer"
                        />
                        <span className="text-sm text-foreground group-hover:text-primary transition-colors duration-200">
                          {option.label}
                        </span>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {option.count}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default FilterSidebar;